import { array } from '@ember/helper';
import { htmlSafe } from '@ember/template';

import type { PopupData } from 'webapp-types';

import joinClasses from 'webapp/helpers/join-classes';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

export default class ForecastsPopupsCorrelationTooltipComponent extends PopupInnerContextComponent<PopupData.Forecast.CorrelationTooltip> {
	get correlationInfo() {
		const value = this.popupData.correlation;
		const absValue = Math.abs(value);
		const isNegative = value < 0;
		const displayValue = (value >= 0 ? '+' : '') + value.toFixed(2);

		if (absValue >= 1.0) {
			const title = `Historical trends indicate a <span class="popup-correlation-tooltip__highlight--blue">perfect ${isNegative ? 'negative' : 'positive'} correlation</span> with the forecasted metric`;
			return {
				type: 'perfect',
				colorClass: 'blue',
				displayValue,
				title: htmlSafe(title),
				description: `This metric will strongly influence the forecast in the ${isNegative ? 'opposite' : 'same'} direction. When it goes ${isNegative ? 'down' : 'up'}, the main KPI goes up at a similar rate.`,
			};
		}

		if (absValue >= 0.7) {
			const title = `Historical trends indicate a <span class="popup-correlation-tooltip__highlight--green">strong ${isNegative ? 'negative' : 'positive'} correlation</span> with the forecasted metric`;
			return {
				type: 'strong',
				colorClass: 'green',
				displayValue,
				title: htmlSafe(title),
				description: `This metric will strongly impact the forecast in the ${isNegative ? 'opposite' : 'same'} direction. When it ${isNegative ? 'decreases' : 'increases'}, the main KPI usually ${isNegative ? 'increases' : 'increases as well'}, though at a slightly lower rate.`,
			};
		}

		if (absValue >= 0.3) {
			const title = `Historical trends indicates a <span class="popup-correlation-tooltip__highlight--orange">moderate ${isNegative ? 'negative' : 'positive'} correlation</span> with the forecasted metric`;
			return {
				type: 'moderate',
				colorClass: 'orange',
				displayValue,
				title: htmlSafe(title),
				description: `This metric will moderately impact the forecast in the ${isNegative ? 'opposite' : 'same'} direction. When it ${isNegative ? 'decreases' : 'increases'}, the main KPI often ${isNegative ? 'increases' : 'increases as well'}, but at a noticeably lower rate.`,
			};
		}

		if (absValue >= 0.1) {
			const title = `Historical trends indicate a <span class="popup-correlation-tooltip__highlight--red">weak ${isNegative ? 'negative' : 'positive'} correlation</span> with the forecasted metric`;
			return {
				type: 'weak',
				colorClass: 'red',
				displayValue,
				title: htmlSafe(title),
				description: `This metric has a slight effect on the forecast in the ${isNegative ? 'opposite' : 'same'} direction. ${isNegative ? 'Decreases' : 'Increases'} in this metric may lead to small increases in the main KPI.`,
			};
		}

		const title = `Historical trends indicate there is <span class="popup-correlation-tooltip__highlight--neutral">no correlation</span> with the forecasted metric`;
		return {
			type: 'no',
			colorClass: 'neutral',
			displayValue,
			title: htmlSafe(title),
			description:
				"This metric has no noticeable effect on the forecast. Changes in this metric don't impact the main KPI in any consistent way.",
		};
	}
	<template>
		<div class="popup-correlation-tooltip">
			<span
				class={{joinClasses
					(array
						"popup-correlation-tooltip__value"
						"correlation-value-badge"
						this.correlationInfo.colorClass
					)
				}}
			>
				{{this.correlationInfo.displayValue}}
			</span>
			<p class="popup-correlation-tooltip__title">
				{{{this.correlationInfo.title}}}
			</p>
			<p class="popup-correlation-tooltip__description">
				{{this.correlationInfo.description}}
			</p>
		</div>
	</template>
}
